import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { MessageCircle, Send, Trash2, XCircle, Phone, Mail, User, Loader2 } from 'lucide-react';

const fmtHora = (iso) => {
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
  } catch { return iso; }
};

const ChatTab = () => {
  const [sesiones, setSesiones] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detalle, setDetalle] = useState(null); // {sesion, mensajes}
  const [respuesta, setRespuesta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const scrollRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/chat/sesiones');
      setSesiones(r.data);
    } catch { /* silencioso */ }
  }, []);

  const cargarDetalle = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/superadmin/chat/sesiones/${id}`);
      setDetalle(r.data);
      // También refresca lista para actualizar "unread"
      cargar();
    } catch (err) { toast.error('Error al cargar la conversación'); }
  }, [cargar]);

  // Polling cada 5s
  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 5000);
    return () => clearInterval(t);
  }, [cargar]);

  // Al cambiar selección, cargar detalle
  useEffect(() => {
    if (selected) cargarDetalle(selected);
    else setDetalle(null);
  }, [selected, cargarDetalle]);

  // Polling del detalle
  useEffect(() => {
    if (!selected) return undefined;
    const t = setInterval(() => cargarDetalle(selected), 5000);
    return () => clearInterval(t);
  }, [selected, cargarDetalle]);

  // Scroll al final cuando cambian mensajes
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detalle]);

  const responder = async (e) => {
    e.preventDefault();
    if (!respuesta.trim() || !selected) return;
    setEnviando(true);
    try {
      await api.post(`/superadmin/chat/sesiones/${selected}/responder`, { contenido: respuesta.trim() });
      setRespuesta('');
      cargarDetalle(selected);
    } catch { toast.error('Error al enviar'); }
    finally { setEnviando(false); }
  };

  const cerrarSesion = async () => {
    if (!selected) return;
    try {
      await api.post(`/superadmin/chat/sesiones/${selected}/cerrar`);
      toast.success('Sesión cerrada');
      cargar(); cargarDetalle(selected);
    } catch { toast.error('Error'); }
  };

  const eliminar = async () => {
    if (!selected) return;
    if (!window.confirm('¿Eliminar esta conversación? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/superadmin/chat/sesiones/${selected}`);
      setSelected(null); setDetalle(null);
      cargar();
      toast.success('Conversación eliminada');
    } catch { toast.error('Error'); }
  };

  const totalUnread = sesiones.reduce((s, x) => s + (x.unread_admin || 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="sa-chat-tab">
      {/* Lista de sesiones */}
      <Card className="border-zinc-200 lg:col-span-1 h-[70vh] flex flex-col">
        <CardHeader className="pb-3 shrink-0">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <MessageCircle className="h-4 w-4" /> Conversaciones
            {totalUnread > 0 && (
              <Badge className="bg-red-600 text-white ml-auto" data-testid="chat-unread-badge">{totalUnread} sin leer</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 flex-1 overflow-y-auto">
          {sesiones.length === 0 && <p className="text-sm text-zinc-400 p-4 text-center">Aún no hay conversaciones.</p>}
          <div className="divide-y divide-zinc-100">
            {sesiones.map((s) => {
              const activo = selected === s.id;
              return (
                <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`chat-sesion-${s.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-zinc-50 flex items-start gap-3 ${activo ? 'bg-zinc-100' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold ${
                    s.estado === 'cerrada' ? 'bg-zinc-400' : 'bg-zinc-900'
                  }`}>
                    {s.visitor_name.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm text-zinc-900 truncate">{s.visitor_name}</p>
                      {s.unread_admin > 0 && (
                        <span className="w-5 h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                          {s.unread_admin}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-500 truncate">{s.visitor_email || s.visitor_phone || 'sin contacto'}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">{fmtHora(s.last_activity_at)} {s.estado === 'cerrada' && '· cerrada'}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Detalle */}
      <Card className="border-zinc-200 lg:col-span-2 h-[70vh] flex flex-col">
        {!detalle ? (
          <CardContent className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
            Selecciona una conversación
          </CardContent>
        ) : (
          <>
            <CardHeader className="pb-3 border-b border-zinc-100 shrink-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <User className="h-4 w-4" /> {detalle.sesion.visitor_name}
                    {detalle.sesion.estado === 'cerrada' && <Badge variant="outline">Cerrada</Badge>}
                  </CardTitle>
                  <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-zinc-600">
                    {detalle.sesion.visitor_email && (
                      <a href={`mailto:${detalle.sesion.visitor_email}`} className="flex items-center gap-1 hover:text-zinc-900">
                        <Mail className="h-3 w-3" /> {detalle.sesion.visitor_email}
                      </a>
                    )}
                    {detalle.sesion.visitor_phone && (
                      <a href={`tel:${detalle.sesion.visitor_phone}`} className="flex items-center gap-1 hover:text-zinc-900">
                        <Phone className="h-3 w-3" /> {detalle.sesion.visitor_phone}
                      </a>
                    )}
                    <span className="text-zinc-400">Inicio: {fmtHora(detalle.sesion.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {detalle.sesion.estado !== 'cerrada' && (
                    <Button size="sm" variant="outline" onClick={cerrarSesion} data-testid="chat-cerrar-btn">
                      <XCircle className="h-4 w-4 mr-1" /> Cerrar
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-red-600" onClick={eliminar} data-testid="chat-eliminar-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-zinc-50 space-y-2.5" data-testid="chat-detalle-scroll">
              {detalle.mensajes.map((m) => (
                <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    m.sender === 'admin'
                      ? 'bg-zinc-900 text-white rounded-br-sm'
                      : 'bg-white text-zinc-900 border border-zinc-200 rounded-bl-sm'
                  }`}>
                    <div>{m.contenido}</div>
                    <div className={`text-[10px] mt-1 ${m.sender === 'admin' ? 'text-white/50' : 'text-zinc-400'}`}>{fmtHora(m.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>

            {detalle.sesion.estado !== 'cerrada' && (
              <form onSubmit={responder} className="p-3 border-t border-zinc-100 flex items-center gap-2 shrink-0">
                <input value={respuesta} onChange={(e) => setRespuesta(e.target.value)}
                  placeholder="Escribe tu respuesta..."
                  className="flex-1 px-3 py-2 rounded-full border border-zinc-200 bg-zinc-50 text-sm"
                  data-testid="chat-respuesta-input" />
                <Button type="submit" disabled={!respuesta.trim() || enviando} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="chat-enviar-btn">
                  {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ChatTab;
